import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Gavel } from "lucide-react";
import axios from "axios";
import { fmtLongDate } from "../lib/dates";

/**
 * Public, read-only Board Book at `/board-book/:token`.
 *
 * No auth cookie. No app shell (we're outside the AuthShell layout — see
 * App.js). Deliberately narrow surface: only the minutes JSON + chair name
 * are fetched. Uses axios directly (not the app's api client) so the
 * anonymous X-User-Id / session cookie is not sent, keeping the request
 * genuinely unauthenticated even from a signed-in browser.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BoardBookPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/board-book/public/${token}`, {
          withCredentials: false,
        });
        if (!cancelled) setState({ status: "ok", data: r.data });
      } catch (e) {
        const code = e?.response?.data?.detail?.code || "not_found";
        const message = e?.response?.data?.detail?.message || "This Board Book link is no longer available.";
        if (!cancelled) setState({ status: "error", code, message });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#888] flex items-center justify-center">
        Loading Board Book…
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#bbb] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="label-eyebrow text-[#c89f45]">Boardroom</p>
          <h1 className="font-serif text-3xl mt-3">Link unavailable</h1>
          <p className="text-sm text-[#888] mt-4">{state.message}</p>
          <a
            href="https://foundtheroom.com"
            className="inline-block mt-8 text-xs font-mono uppercase tracking-widest text-[#c89f45] hover:underline"
          >
            Learn about Boardroom →
          </a>
        </div>
      </div>
    );
  }

  const { meeting, chair_name, expires_at } = state.data;
  const m = meeting.minutes;
  const dateStr = fmtLongDate(meeting.completed_at || meeting.created_at);
  const expiresStr = new Date(expires_at).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-10">
      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 16mm; }
          html, body { background: #ffffff !important; color: #111 !important; }
          .no-print { display: none !important; }
          .board-book-page { color: #111 !important; background: #ffffff !important; box-shadow: none !important; }
          .board-book-page * { color: inherit !important; }
          .board-book-section { break-inside: avoid; page-break-inside: avoid; }
          .board-book-cover { break-after: page; page-break-after: always; }
        }
        .board-book-page { font-family: 'Fraunces', 'Cormorant Garamond', 'Georgia', serif; }
      `}</style>

      {/* Screen-only control bar */}
      <div className="no-print max-w-3xl mx-auto px-6 flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#c89f45]">
            Boardroom · Shared Board Book
          </p>
          <p className="text-[10px] font-mono text-[#555] mt-1">
            Read-only · Link valid until {expiresStr}
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-4 py-2 text-sm transition-colors"
          data-testid="board-book-public-print"
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <article className="board-book-page max-w-3xl mx-auto bg-white text-[#111] rounded-md shadow-2xl p-10 sm:p-14 print:p-0 print:shadow-none">
        {/* Cover */}
        <section className="board-book-cover text-center py-10">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-sm border border-[#c89f45] mb-6">
            <Gavel size={22} style={{ color: "#8a6c1f" }} />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: "#8a6c1f" }}>
            Board Book · Quarterly Review
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight">{meeting.title}</h1>
          <p className="mt-4 text-sm font-mono text-[#555]">
            {dateStr}
            {meeting.duration_seconds ? ` · ${Math.round(meeting.duration_seconds / 60)} min on the record` : ""}
          </p>
          <div className="mt-14 flex justify-center">
            <div className="w-40 h-px bg-[#c89f45] opacity-60" />
          </div>
          <p className="mt-6 text-xs font-mono uppercase tracking-widest text-[#666]">
            Chairperson · {chair_name}
          </p>
        </section>

        {m.executive_summary && (
          <BookSection eyebrow="§ 1 · Executive Summary" title="The state of things">
            <p className="text-lg leading-relaxed text-[#222]">{m.executive_summary}</p>
          </BookSection>
        )}
        <BookList eyebrow="§ 2 · Wins" title="What worked" items={m.wins} />
        <BookList eyebrow="§ 3 · Losses & Lessons" title="What was learned" items={m.lessons} />
        <BookList eyebrow="§ 4 · Goals" title="Progress on quarter goals" items={m.goals_progress} />

        {m.financial_snapshot && (
          <BookSection eyebrow="§ 5 · Financial Review" title="The money picture">
            <p className="text-base leading-relaxed">{m.financial_snapshot}</p>
          </BookSection>
        )}
        {Array.isArray(m.metric_trends) && m.metric_trends.length > 0 && (
          <BookList eyebrow="§ 6 · Metric Trends" title="What the numbers said" items={m.metric_trends} />
        )}
        {Array.isArray(m.habit_scorecard) && m.habit_scorecard.length > 0 && (
          <BookList eyebrow="§ 7 · Habit Scorecard" title="90-day habit audit" items={m.habit_scorecard} />
        )}

        {m.category_reflection && typeof m.category_reflection === "object" && (
          <BookSection eyebrow="§ 8 · Category Reflection" title="The 5 pillars">
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              {["health", "business", "relationships", "money", "craft"].map((k) => {
                const val = (m.category_reflection[k] || "").trim();
                if (!val) return null;
                return (
                  <div key={k} className="rounded-md p-4 border border-[#ddd] bg-[#faf7f0]">
                    <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8a6c1f" }}>
                      {k}
                    </p>
                    <p className="text-sm mt-2 leading-relaxed text-[#222]">{val}</p>
                  </div>
                );
              })}
            </div>
          </BookSection>
        )}

        {Array.isArray(m.strategic_shifts) && m.strategic_shifts.length > 0 && (
          <BookList eyebrow="§ 9 · Strategic Shifts" title="Pivots, doubles-downs, kills" items={m.strategic_shifts} />
        )}
        {m.vision_alignment && (
          <BookSection eyebrow="§ 10 · Vision Alignment" title="North Star check">
            <p className="text-base leading-relaxed">{m.vision_alignment}</p>
          </BookSection>
        )}
        {m.quarter_pattern_analysis && (
          <BookSection eyebrow="§ 11 · Pattern Analysis" title="What the three monthlies revealed">
            <p className="text-base leading-relaxed">{m.quarter_pattern_analysis}</p>
          </BookSection>
        )}

        {Array.isArray(m.next_quarter_bets) && m.next_quarter_bets.length > 0 && (
          <BookSection eyebrow="§ 12 · Next Quarter's Bets" title="Where the chips are going">
            <ol className="mt-2 space-y-4">
              {m.next_quarter_bets.map((bet, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-serif text-xl leading-none shrink-0" style={{ color: "#8a6c1f" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <p className="font-serif text-lg">{bet.title}</p>
                    {bet.description && <p className="text-sm mt-1.5 leading-relaxed">{bet.description}</p>}
                    {bet.why_it_matters && (
                      <p className="text-xs mt-2 leading-relaxed italic text-[#555]">
                        Why it matters — {bet.why_it_matters}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </BookSection>
        )}

        {Array.isArray(m.action_items) && m.action_items.length > 0 && (
          <BookSection eyebrow="§ 13 · Action Items" title="Commitments on the record">
            <ol className="mt-2 space-y-2 list-decimal list-inside">
              {m.action_items.map((a, i) => (
                <li key={i} className="text-base leading-relaxed">
                  <span className="font-medium">{a.title}</span>
                  {a.description ? <span className="text-[#555]"> — {a.description}</span> : null}
                </li>
              ))}
            </ol>
          </BookSection>
        )}

        {m.next_focus && (
          <BookSection eyebrow="§ 14 · Next Focus" title="The one thing next quarter must nail">
            <p className="font-serif text-xl leading-snug">{m.next_focus}</p>
          </BookSection>
        )}

        <section className="board-book-section mt-16">
          <div className="border-t border-[#ccc] pt-10">
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#555]">Signed · Chairperson</p>
                <div className="mt-6 h-10 border-b border-[#333]" />
                <p className="mt-3 font-serif text-base">{chair_name}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#555]">Date</p>
                <div className="mt-6 h-10 border-b border-[#333]" />
                <p className="mt-3 font-serif text-base">{dateStr}</p>
              </div>
            </div>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-center text-[#888] mt-12">
            Boardroom · foundtheroom.com · Session integrity is everything
          </p>
        </section>
      </article>
    </div>
  );
}

function BookSection({ eyebrow, title, children }) {
  return (
    <section className="board-book-section mt-12">
      <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8a6c1f" }}>
        {eyebrow}
      </p>
      <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug">{title}</h2>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function BookList({ eyebrow, title, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <BookSection eyebrow={eyebrow} title={title}>
      <ul className="mt-2 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-base leading-relaxed flex gap-3">
            <span className="shrink-0" style={{ color: "#8a6c1f" }}>·</span>
            <span>{typeof it === "string" ? it : JSON.stringify(it)}</span>
          </li>
        ))}
      </ul>
    </BookSection>
  );
}
